/**
 * React Query hooks for PlanInstance and calendar entry management.
 * Replaces Zustand mock data for Plan Calendar operations.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createPlanInstance,
  deletePlanInstance,
  fetchPlanInstances,
  fetchCalendarRange,
  addCalendarEntry,
  updateCalendarEntry,
  deleteCalendarEntry,
  bulkAddCalendarEntries,
  type PlanInstanceCreate,
  type CalendarEntryCreate,
  type CalendarEntryUpdate,
} from "@/lib/templateApi";
import { useAuthState } from "./useAuthState";

const INSTANCE_KEY = "planInstances";
const CALENDAR_KEY = "planCalendar";

// ── Queries ───────────────────────────────────────────────────────────────────

export function usePlanInstances(params?: { athlete_id?: string; status?: string }) {
  const { isLoaded, isAuthenticated } = useAuthState();

  return useQuery({
    queryKey: [INSTANCE_KEY, params],
    queryFn: () => fetchPlanInstances(params),
    enabled: isLoaded && isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePlanCalendar(instanceId: string | null, startDate: string, endDate: string) {
  const { isLoaded, isAuthenticated } = useAuthState();

  return useQuery({
    queryKey: [CALENDAR_KEY, instanceId, startDate, endDate],
    queryFn: () => fetchCalendarRange(instanceId!, startDate, endDate),
    enabled: isLoaded && isAuthenticated && !!instanceId && !!startDate && !!endDate,
  });
}

// ── Instance mutations ────────────────────────────────────────────────────────

export function useCreatePlanInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PlanInstanceCreate) => createPlanInstance(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [INSTANCE_KEY] });
    },
  });
}

export function useDeletePlanInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePlanInstance(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [INSTANCE_KEY] });
      qc.invalidateQueries({ queryKey: [CALENDAR_KEY] });
    },
  });
}

// ── Calendar mutations ────────────────────────────────────────────────────────

export function useAddCalendarEntry(instanceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CalendarEntryCreate) => addCalendarEntry(instanceId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CALENDAR_KEY, instanceId] });
    },
  });
}

export function useUpdateCalendarEntry(instanceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workoutInstanceId, payload }: { workoutInstanceId: string; payload: CalendarEntryUpdate }) =>
      updateCalendarEntry(instanceId, workoutInstanceId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CALENDAR_KEY, instanceId] });
    },
  });
}

export function useDeleteCalendarEntry(instanceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (workoutInstanceId: string) => deleteCalendarEntry(instanceId, workoutInstanceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CALENDAR_KEY, instanceId] });
    },
  });
}

export function useBulkAddCalendarEntries(instanceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entries: { workout_template_id: string; scheduled_date: string; scheduled_time?: string }[]) =>
      bulkAddCalendarEntries(instanceId, entries),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CALENDAR_KEY, instanceId] });
    },
  });
}
